import { useAppDispatch } from "@/services/hooks";
import React, { useCallback, useEffect, useState } from "react";

import ReactBSAlert from "react-bootstrap-sweetalert";
import NotificationAlertOptions from "@/layouts/Alerts";
import {
  updateSettingVariables,
  deleteSettingsVariables,
} from "@/services/siteServices/siteActions";
import {
  CONTACT_EMAIL_VARIABLE,
  ensureContactEmailVariable,
  isValidContactEmail,
} from "@/utils/siteSettings";
import UpdateSiteConfigurationsView from "./UpdateSiteConfigurationsView";
import AddSiteConfigurationsContainer from "./AddSiteConfigurationsContainer";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import { asCaught } from "@/services/caughtError";

/** One site setting as the list edits it: the variable name and its value. */
type SettingVariable = { variable?: string; value?: unknown };

export type UpdateSiteConfigurationsContainerProps = {
  SettingsVariables?: SettingVariable[];
  current?: string;
  action?: "update" | "add";
};

const UpdateSiteConfigurationsContainer = ({
  SettingsVariables = [],
  current = "",
  action: initialAction = "update", 
}: UpdateSiteConfigurationsContainerProps) => {
  const dispatch = useAppDispatch();
  const permission = useCurrentPermission();

  const [action, setAction] = useState(initialAction);
  const [settingsVariables, setSettingsVariables] = useState<SettingVariable[]>(() =>
    ensureContactEmailVariable(
      Array.isArray(SettingsVariables) ? SettingsVariables : []
    )
  );
  const [alertNode, setAlertNode] = useState<React.ReactNode>(null);
  const [contactEmailInvalid, setContactEmailInvalid] = useState(false);
  
  // Sync from Redux only when the server data changes — not on every keystroke.
  useEffect(() => {
    setSettingsVariables(
      ensureContactEmailVariable(
        Array.isArray(SettingsVariables) ? SettingsVariables : []
      )
    );
  }, [SettingsVariables]);

  const onChangeSettings = useCallback((e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { value } = e.target;
    setSettingsVariables((prev) => {
      const next = prev.map((prop, key) => {
        if (key === index) {
          const updated = { ...prop };
          updated.value = value;
          return updated;
        }
        return prop;
      });
      if (prev[index]?.variable === CONTACT_EMAIL_VARIABLE) {
        setContactEmailInvalid(false);
      }
      return next;
    });
  }, []);

  const onClickUpdateSettings = useCallback(async () => {
    const contactEmailEntry = settingsVariables.find(
      (entry) => entry.variable === CONTACT_EMAIL_VARIABLE
    );
    if (!isValidContactEmail(contactEmailEntry?.value)) {
      setContactEmailInvalid(true);
      NotificationAlertOptions(
        "error",
        "Contact Email is required and must be a valid email address."
      );
      return;
    }

    setContactEmailInvalid(false);
    const data = { settings_variables: settingsVariables };

    try {
      await updateSettingVariables(data)(dispatch);
      NotificationAlertOptions("success", "Configuration Updated with success!");
    } catch (error) {
      const serverMessage =
        asCaught(error)?.response?.data?.error || asCaught(error)?.response?.data?.message;
      console.error("[UpdateSiteConfigurations] Save failed:", {
        message: asCaught(error)?.message,
        serverMessage,
        status: asCaught(error)?.response?.status,
      });
      NotificationAlertOptions(
        "error",
        serverMessage ||
          "Failed to save configuration. The server may be unavailable."
      );
    }
  }, [dispatch, settingsVariables]);

  const onClickAddSettings = useCallback(() => {
    setAction("add");
  }, []);

  const hideAlert = useCallback(() => setAlertNode(null), []);

  const onClickDeleteAsk = useCallback(() => {
    setAlertNode(
      // @ts-expect-error react-bootstrap-sweetalert prop-types (customIcon/btnSize)
      <ReactBSAlert
        custom
        style={{ display: "block", marginTop: "-100px" }}
        title="Would you like to remove current site configurations?"
        customIcon={
          <div
            className="swal2-icon swal2-question swal2-animate-question-icon"
            style={{ display: "flex" }}
          >
            <span className="swal2-icon-text">?</span>
          </div>
        }
        onConfirm={async () => {
          // Only confirm + refetch when the delete succeeds; deleteSettingsVariables now
          // re-throws on failure (SCRUM-815).
          try {
            // SCRUM-1225: a dispatch(getSettingVariables()) followed here,
            // refetching the design-level catalogue that no screen rendered.
            // deleteSettingsVariables already clears SettingsVariables, which is
            // the list this screen actually shows.
            await dispatch(deleteSettingsVariables());
            hideAlert();
            NotificationAlertOptions("success", "Configuration Deleted with success!");
          } catch (err) {
            hideAlert();
            NotificationAlertOptions(
              "error",
              asCaught(err)?.response?.data?.message || "Deleting the configuration failed. Please try again."
            );
          }
        }}
        onCancel={hideAlert}
        confirmBtnBsStyle="default"
        confirmBtnText="Ok"
        btnSize=""
      >
        Delete Operation
      </ReactBSAlert>
    );
  }, [dispatch, hideAlert]);

  
  if (action === "add") {
    return (
      <AddSiteConfigurationsContainer
        current={current}
        SettingsVariables={settingsVariables}
        action="add"
      />
    );
  }

 
  return (
    <UpdateSiteConfigurationsView
      current={current}
      permission={permission}
      settingsVariables={settingsVariables}
      onChangeSettings={onChangeSettings}
      onClickAddSettings={onClickAddSettings}
      onClickUpdateSettings={onClickUpdateSettings}
      onClickDeleteAsk={onClickDeleteAsk}
      alertNode={alertNode}
      contactEmailInvalid={contactEmailInvalid}
    />
  );
};

export default UpdateSiteConfigurationsContainer;
